import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDB } from "@/db";
import { z } from "zod";
import { request } from "@/lib/request";
import axios, { isAxiosError } from "axios";
import { deleteRequestConfig } from "./requestsSlice";

export const tableSettingsFormSchema = z.object({
  arrayField: z.string(),
  countField: z.string(),
  title: z.string().default("Custom Table"),
  columns: z.array(
    z.object({
      enabled: z.boolean(),
      fieldName: z.string(),
      columnName: z.string(),
      columnType: z.enum([
        "string",
        "number",
        "boolean",
        "date",
        "array",
        "object",
        "unknown",
      ]),
      columnDisplay: z.string().default("default"),
      columnDisplayProps: z.record(z.unknown()).default({}),
      columnStyle: z
        .object({
          display: z.string().default("default"),
          color: z.string().default("default"),
          backgroundColor: z.string().default("default"),
          fontSize: z.number().default(14),
          fontWeight: z.string().default("default"),
          fontStyle: z.array(z.enum(["italic"])).default([]),
          textDecoration: z.string().default("default"),
        })
        .default({}),
    }),
  ),
});

export type TableConfigType = z.infer<typeof tableSettingsFormSchema> & {
  id: string;
  requestId: string;
};

export interface AdvancedTableState {
  tables: Record<string, TableConfigType>;
  initialized: boolean;
}

const initialState: AdvancedTableState = {
  tables: {},
  initialized: false,
};

const handleApiError = (err: unknown, message: string) => {
  if (!isAxiosError(err) || !err.status?.toString().startsWith("4")) {
    console.error(message, err);
  }
};

export const initializeTables = createAsyncThunk(
  "advancedTable/initialize",
  async (_, { dispatch }) => {
    const tableConfigs = await (await getDB()).getAll("tableConfigurations");
    if (request.defaults.headers.common.Authorization) {
      dispatch(syncBackend());
    }
    return tableConfigs || [];
  },
);

export const syncBackend = createAsyncThunk(
  "advancedTable/syncBackend",
  async () => {
    const response = await request.get<{ data: TableConfigType[] }>(
      "/internal/table",
    );
    const tx = (await getDB()).transaction("tableConfigurations", "readwrite");
    await Promise.all([
      ...response.data.data.map(tableConfig => tx.store.put(tableConfig)),
      tx.done,
    ]);

    return response.data.data;
  },
);

export const setTableConfig = createAsyncThunk(
  "advancedTable/setTableConfig",
  async ({ tableConfig }: { tableConfig: TableConfigType }, { dispatch }) => {
    await (await getDB()).put("tableConfigurations", tableConfig);
    dispatch(updateTableAPI({ tableConfig }));
    return tableConfig;
  },
);

export const deleteTable = createAsyncThunk(
  "advancedTable/deleteTable",
  async (
    { id, deleteRequestId }: { id: string; deleteRequestId?: string },
    { dispatch },
  ) => {
    await (await getDB()).delete("tableConfigurations", id);
    dispatch(deleteTableAPI({ id, deleteRequestId }));
    return id;
  },
);

export const createTableAPI = createAsyncThunk(
  "advancedTable/createBackend",
  async ({ tableConfig }: { tableConfig: TableConfigType }) => {
    const response = await request.post(`/internal/table`, tableConfig);
    return response.data.data;
  },
);

export const updateTableAPI = createAsyncThunk(
  "advancedTable/updateBackend",
  async ({ tableConfig }: { tableConfig: TableConfigType }, { dispatch }) => {
    try {
      const response = await request.put(
        `/internal/table/${tableConfig.id}`,
        tableConfig,
      );

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.status === 404) {
        dispatch(createTableAPI({ tableConfig }));
      } else {
        throw error;
      }
    }
  },
);

export const deleteTableAPI = createAsyncThunk(
  "advancedTable/deleteBackend",
  async (
    { id, deleteRequestId }: { id: string; deleteRequestId?: string },
    { dispatch },
  ) => {
    await request.delete(`/internal/table/${id}`);

    if (deleteRequestId) {
      dispatch(deleteRequestConfig({ id: deleteRequestId }));
    }

    return id;
  },
);

const advancedTableSlice = createSlice({
  name: "advancedTable",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(initializeTables.fulfilled, (state, action) => {
        state.tables = action.payload.reduce<AdvancedTableState["tables"]>(
          (acc, tableConfig) => {
            acc[tableConfig.id] = tableConfig;

            return acc;
          },
          {},
        );
        state.initialized = true;
      })
      .addCase(initializeTables.rejected, (state, action) => {
        state.tables = {};
        state.initialized = true;
        console.error("Failed to initialize tables:", action.error);
      })
      .addCase(syncBackend.fulfilled, (state, action) => {
        action.payload?.map((table: TableConfigType) => {
          state.tables[table.id] = table;
        });
      })
      .addCase(syncBackend.rejected, (_, action) => {
        console.error("Failed to sync tables with backend:", action.error);
      })
      .addCase(setTableConfig.fulfilled, (state, action) => {
        if (!action.payload?.id) {
          return;
        }

        state.tables[action.payload.id] = action.payload;
      })
      .addCase(setTableConfig.rejected, (_, action) => {
        console.error("Failed to set table config:", action.error);
      })
      .addCase(deleteTable.fulfilled, (state, action) => {
        delete state.tables[action.payload];
      })
      .addCase(deleteTable.rejected, (_, action) => {
        console.error("Failed to delete table config:", action.error);
      })
      .addCase(updateTableAPI.fulfilled, (state, action) => {
        if (!action.payload?.id) {
          return;
        }

        state.tables[action.payload.id] = action.payload;
      })
      .addCase(createTableAPI.fulfilled, (state, action) => {
        if (!action.payload?.id) {
          return;
        }

        state.tables[action.payload.id] = action.payload;
      })
      .addCase(deleteTableAPI.fulfilled, (state, action) => {
        delete state.tables[action.payload];
      })
      .addCase(updateTableAPI.rejected, (_, action) => {
        handleApiError(action.error, "Failed to update table config:");
      })
      .addCase(createTableAPI.rejected, (_, action) => {
        handleApiError(action.error, "Failed to create table config:");
      })
      .addCase(deleteTableAPI.rejected, (_, action) => {
        handleApiError(action.error, "Failed to delete request config:");
      });
  },
});

export default advancedTableSlice.reducer;
